import { FC, memo, useMemo } from "react";
import { ISupportTicket } from "../../../common/models/support/ISupport";
import { timeFormatter } from "../../../common/utils/helpers";
import { Title } from "../../../common/components/Title/Title";
import { formatVoiceDuration } from "./VoicePlayer";

interface ISupportTicketListProps {
  tickets: ISupportTicket[];
  activeTicketId?: number;
  onItemClick: (id: number) => void;
}

export function getTicketUserName(ticket: ISupportTicket): string {
  const user = ticket.user;
  if (!user) return "Пользователь удалён";
  const name = [user.last_name, user.first_name, user.middle_name]
    .filter(Boolean)
    .join(" ");
  return name || user.phone || `Пользователь № ${user.id}`;
}

function getPreview(ticket: ISupportTicket): string {
  const message = ticket.last_message;
  if (!message) return "Сообщений нет";
  const body = message.voice
    ? `Голосовое сообщение, ${formatVoiceDuration(message.voice.duration_ms)}`
    : message.text ?? "";
  return message.sender === "support" ? `Вы: ${body}` : body;
}

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const SupportTicketCard: FC<{
  ticket: ISupportTicket;
  isActive: boolean;
  onClick: () => void;
}> = memo(({ ticket, isActive, onClick }) => {
  const date = new Date(ticket.last_message_at ?? ticket.created_at);
  const time = timeFormatter({ time: date, mode: "hh:mm" });

  return (
    <button
      type="button"
      className="w-full min-w-0 text-left"
      onClick={onClick}
    >
      <div
        className={`flex flex-col rounded border p-[12px] ${
          isActive ? "border-primary" : "border-whiteGray"
        } bg-lightWhiteGray transition`}
      >
        <div className="mb-[3px] flex items-center justify-between">
          <h4 className="truncate text-sm font-medium">
            {getTicketUserName(ticket)}
          </h4>
          <time className="ml-[8px] shrink-0 text-xs text-lightGray">
            {time}
          </time>
        </div>
        <p className="mb-[4px] truncate text-xs text-gray">
          {getPreview(ticket)}
        </p>
        <div className="flex items-center justify-between">
          <div className="text-xs leading-4 text-primary">
            Обращение № {ticket.id}
          </div>
          {ticket.unread_count > 0 && (
            <div
              aria-label={`Непрочитанных: ${ticket.unread_count}`}
              className="flex h-[15px] min-w-[15px] items-center justify-center rounded-[50%] bg-primary px-[3px] text-xxs font-medium text-white"
            >
              <span className="mt-[2px]">{ticket.unread_count}</span>
            </div>
          )}
        </div>
      </div>
    </button>
  );
});

/** Обращения по дням последнего сообщения, свежие сверху (порядок отдаёт сервер) */
export const SupportTicketList: FC<ISupportTicketListProps> = memo(
  ({ tickets, activeTicketId, onItemClick }) => {
    const groups = useMemo(() => {
      const result: { key: string; date: Date; items: ISupportTicket[] }[] = [];
      tickets.forEach((ticket) => {
        const date = new Date(ticket.last_message_at ?? ticket.created_at);
        const last = result[result.length - 1];
        if (last && isSameDay(last.date, date)) {
          last.items.push(ticket);
        } else {
          result.push({ key: String(ticket.id), date, items: [ticket] });
        }
      });
      return result;
    }, [tickets]);

    const today = new Date();

    return (
      <div className="mb-[15px] grid h-fit grid-cols-[minmax(0,1fr)] gap-[12px]">
        {groups.map((group) => (
          <div
            className="grid w-full grid-cols-[minmax(0,1fr)] gap-[10px]"
            key={group.key}
          >
            <Title>
              {isSameDay(group.date, today)
                ? "Здесь и сейчас"
                : timeFormatter({ time: group.date, mode: "dd-Mth-yyyy" })}
            </Title>
            {group.items.map((ticket) => (
              <SupportTicketCard
                key={ticket.id}
                ticket={ticket}
                isActive={ticket.id === activeTicketId}
                onClick={() => onItemClick(ticket.id)}
              />
            ))}
          </div>
        ))}
      </div>
    );
  }
);
